import { orbitRevolutionsPerBuffer } from "./orbit-harmonics";

export const ORBIT_DESKTOP_COMP_LIST_PERCENT = 1;

const DESKTOP_SPECTRUM_LINE_AMP_SCALE = 2;

export interface IDesktopOrbitComponentRow {
  binIndex: number;
  harmonicRev: number;
  ampX: number;
  ampY: number;
  s: number;
}

export function getOrbitPeakRadius(xData: number[], yData: number[]): number {
  const n = xData.length;
  let maxR = 0;
  for (let i = 0; i < n; i += 1) {
    maxR = Math.max(maxR, Math.hypot(xData[i], yData[i]));
  }
  return maxR;
}

function spectrumMagnitudesAC(ac: number[]): number[] {
  const n = ac.length;
  const half = Math.floor(n / 2);
  const out = new Array<number>(half).fill(0);
  for (let k = 1; k < half; k += 1) {
    const omega = (2 * Math.PI * k) / n;
    let a = 0;
    let b = 0;
    for (let t = 0; t < n; t += 1) {
      const c = Math.cos(omega * t);
      const s = Math.sin(omega * t);
      a += ac[t] * c;
      b += ac[t] * s;
    }
    a = (2 * a) / n;
    b = (2 * b) / n;
    out[k] = Math.hypot(a, b) * DESKTOP_SPECTRUM_LINE_AMP_SCALE;
  }
  return out;
}

export function calcDesktopOrbitComponentRows(
  xData: number[],
  yData: number[],
  param3: number,
  resolution: number | undefined,
  compListPercent: number = ORBIT_DESKTOP_COMP_LIST_PERCENT,
): IDesktopOrbitComponentRow[] {
  const n = xData.length;
  if (!n || n !== yData.length || !param3 || param3 <= 0) {
    return [];
  }

  const mx = xData.reduce((a, b) => a + b, 0) / n;
  const my = yData.reduce((a, b) => a + b, 0) / n;
  const xAc = xData.map(v => v - mx);
  const yAc = yData.map(v => v - my);

  const magX = spectrumMagnitudesAC(xAc);
  const magY = spectrumMagnitudesAC(yAc);

  const peak = getOrbitPeakRadius(xData, yData);
  if (peak < 1e-15) {
    return [];
  }
  const thresh = peak * (compListPercent / 100);

  const semantics = "revolutionsPerBuffer" as const;
  const r = orbitRevolutionsPerBuffer(n, resolution, param3, semantics);
  const denom = Math.max(r * 2, 1e-15);

  const bins: number[] = [];
  const len = Math.min(magX.length, magY.length);
  for (let i = 1; i < len; i += 1) {
    if (magY[i] >= thresh || magX[i] >= thresh) {
      bins.push(i);
    }
  }

  const rows: IDesktopOrbitComponentRow[] = [];
  for (const i of bins) {
    rows.push({
      binIndex: i,
      harmonicRev: denom > 1e-15 ? i / denom : i,
      ampX: magX[i],
      ampY: magY[i],
      s: Math.hypot(magX[i], magY[i]),
    });
  }
  return rows;
}
